/**
 * This hook is used to handle the drop event for the steps & operations in the test draft view.
 */

import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { z } from 'zod';
import { api } from '~/trpc-extra/api';
import { useUpdateStepDescription } from '../mutations/steps/useUpdateStepDescription';
import { zCreateOperationParentContext } from '~/server/logic/steps/operation/types';
import { handleMutationSuccess } from '../mutations/callback-handles';

const zOperationDropZoneData = z.object({
  type: z.literal('operation'),
  targetNextOperationId: z.string().optional(),
  targetStepId: z.string(),
  targetParentContext: zCreateOperationParentContext.optional(),
});

const zOperationActionItemData = z.object({
  type: z.literal('operation'),
  operationId: z.string(),
  sourceParentContext: zCreateOperationParentContext.optional(),
  sourceStepId: z.string(),
});

export type OperationActionItemData = z.infer<typeof zOperationActionItemData>;

export const zStepDropZoneData = z.object({
  testContentId: z.string(),
  insertAfterStepOrder: z.number(),
  // TODO: maybe we don't need these
  prevStepId: z.string().optional(),
  nextStepId: z.string().optional(),
});

// TODO: disable dropping when some other active editing (e.g. dropping) is in progress
export function useHandleMoveOperation({ testContentId }: { testContentId: string }) {
  const trpcUtils = api.useUtils();
  const [isMoving, setIsMoving] = useState(false);
  const { mutateAsync: moveOperation } = api.operation.moveOperation.useMutation();
  const { mutateAsync: addStep } = api.testStep.insert.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Step created', color: 'blue' });
    },
    onError: () => {
      notifications.show({ message: 'Error creating step', color: 'red' });
    },
  });

  const { updateStepDescription } = useUpdateStepDescription({ testContentId });

  const handleMoveOperation = async (actionItem: unknown, dropZone: unknown) => {
    const operationDropZoneData = zOperationDropZoneData.safeParse(dropZone);
    const operationActionItemData = zOperationActionItemData.safeParse(actionItem);
    const stepDropZoneData = zStepDropZoneData.safeParse(dropZone);

    if (operationActionItemData.success) {
      const { operationId, sourceStepId, sourceParentContext } = operationActionItemData.data;

      try {
        setIsMoving(true);

        if (operationDropZoneData.success) {
          // If the drop zone is an operation, move the operation
          const { targetNextOperationId, targetParentContext, targetStepId } = operationDropZoneData.data;

          await moveOperation({
            operationId,
            target:
              targetNextOperationId ?
                { type: 'afterOperation', operationId: targetNextOperationId }
              : { type: 'endOfParent', targetStepId, parentContext: targetParentContext },
          });

          // We need to invalidate the source and target parents
          await Promise.allSettled([
            handleMutationSuccess({
              trpcUtils,
              testContentId,
              parentContext: targetParentContext,
            }),
            handleMutationSuccess({
              trpcUtils,
              testContentId,
              parentContext: sourceParentContext,
            }),
          ]);

          // Update step descriptions for both source and target steps
          updateStepDescription({ stepId: targetStepId }).catch(() => undefined);

          // Update step description for the source step if it's not the same as the target step
          if (sourceStepId !== targetStepId) {
            updateStepDescription({ stepId: sourceStepId }).catch(() => undefined);
          }
        } else if (stepDropZoneData.success) {
          // Drop operation into empty space and create a new step
          const newStep = await addStep({
            description: '',
            order: stepDropZoneData.data.insertAfterStepOrder + 1,
            testContentId: stepDropZoneData.data.testContentId,
          });
          await moveOperation({
            operationId,
            target: { type: 'endOfParent', targetStepId: newStep.id },
          });
          // We need to invalidate the source parents
          await handleMutationSuccess({
            trpcUtils,
            testContentId,
            parentContext: sourceParentContext,
          });
          updateStepDescription({ stepId: newStep.id }).catch(() => undefined);
        }
      } catch (error) {
        console.error('Error moving operation', error);
      } finally {
        setIsMoving(false);
      }
    } else {
      console.error('Invalid drop event', {
        source: actionItem,
        target: dropZone,
        sourceError: operationActionItemData.error,
        targetError: operationDropZoneData.error,
      });
      return;
    }

    // Invalidate the test, component, and code queries once again to ensure the UI updates
    await Promise.allSettled([trpcUtils.testContent.get.invalidate(), trpcUtils.testContent.getCode.invalidate()]);
  };

  return { handleMoveOperation, isMoving };
}
